import { LinkedCard } from '@/store/main/StorymapCardDetails/types';

/**
 * Status of a card link entry in the RichTextEditor store.
 * - 'idle': Entry exists but data hasn't been fetched yet
 * - 'loading': Fetch is in progress
 * - 'loaded': Data has been fetched (linkedCard may still be null if card was not found)
 * - 'error': Fetch failed with an error
 */
export type CardLinkStatus = 'idle' | 'loading' | 'loaded' | 'error';

/**
 * An entry in the card links cache.
 * Represents the state of a single card link's data resolution.
 */
export interface CardLinkEntry {
    /** The resolved card data, or null if not yet loaded or not found */
    linkedCard: LinkedCard | null;
    /** Current status of this entry */
    status: CardLinkStatus;
    /** Error message if status is 'error' */
    lastError?: string;
}

/**
 * State for the RichTextEditor store.
 * Currently contains only the card-link slice; additional features can be added later.
 */
export class RichTextEditorState {
    /**
     * Cache of card link entries, keyed by composite key "<slugOrNull>:<cardId>".
     * This provides a centralized cache for all card link data across editors.
     */
    cardLinksByKey: Record<string, CardLinkEntry> = {};
}

/**
 * Creates a composite key for card link cache lookup.
 * @param cardId The card ID
 * @param storymapSlug Optional storymap slug (null for current storymap)
 * @returns A composite key string
 */
export function createCardLinkKey(cardId: string, storymapSlug?: string | null): string {
    return `${storymapSlug || 'current'}:${cardId}`;
}
